/**
 * Skill perks (docs/gameplay/skill-definitions.md, How a Skill Pays Off).
 * Each skill unlocks three passive perks at set levels: Martial at levels 3,
 * 6 and 10, Essence at rings 2, 4 and 5 (levels 4, 8 and 10). A perk is
 * active while its skill is at or above that level, so decay switches a
 * Martial perk off until the skill is retrained. Each skill's issue adds its
 * perks here; their numbers are first-pass, to tune.
 */

import { isEquippedItem } from './skills';
import type { Behavior, BehaviorStackDefinition } from './types/behaviors';
import type { CharacterEquipmentLoadout, Equipment, Skill } from './types/game';

export interface SkillPerkDefinition {
    readonly id: string;
    readonly skillId: string;
    readonly name: string;
    readonly description: string;
    /** The skill level it unlocks at. */
    readonly level: number;
    /**
     * What it does, in the vocabulary named items use (`types/behaviors.ts`),
     * read through `resolveActiveBehaviors`. A perk with none is a rule its
     * own code reads with `hasSkillPerk` (Quick Hands, Mending, Gatherer,
     * Deep Pool).
     */
    readonly behaviors?: readonly Behavior[];
}

/** A weakest blow lands at this share of a weapon's damage range: the impact floor. */
export const BASE_IMPACT_FLOOR = 0.3;

// Blade Mastery (#146)
/** Keen Edge: a blade's glancing hits land at 45% of its range, not 30%. */
export const KEEN_EDGE_IMPACT_FLOOR = 0.45;
/** Flurry: every this-many-th hit in a row on one target is a flurry hit. */
export const FLURRY_HIT_INTERVAL = 3;
/** Flurry: a flurry hit deals this much more damage. */
export const FLURRY_DAMAGE_BONUS = 0.25;
/** Find the Gap: a blade's weak-point hits deal this many times their damage, not 1.5. */
export const FIND_THE_GAP_WEAK_POINT_MULTIPLIER = 1.75;

// Bludgeoning (#147)
/** Crushing Blows: each stack lowers the target's mitigation by this much, and can take it below zero. */
export const CRUSHING_BLOWS_MITIGATION_PER_STACK = 2;
/** Crushing Blows and Unravel share one set of stacks, capped here. */
export const MITIGATION_SHRED_MAX_STACKS = 3;
/** How long mitigation stacks last after the last blow that added one. */
export const MITIGATION_SHRED_DURATION_MS = 6000;
/** Stagger: a hit at least this good (damage quality 0-1) is a strong hit. */
export const STAGGER_MIN_DAMAGE_QUALITY = 0.75;
/** Stagger: a strong hit pushes the target's next attack back this long. */
export const STAGGER_DELAY_MS = 500;

/** Crushing Blows' and Unravel's shared stacks: each lowers mitigation, even below zero. */
export const MITIGATION_SHRED_STACK: BehaviorStackDefinition = {
    stackId: 'mitigation-shred',
    maxStacks: MITIGATION_SHRED_MAX_STACKS,
    durationMs: MITIGATION_SHRED_DURATION_MS,
    perStack: { mitigationShred: CRUSHING_BLOWS_MITIGATION_PER_STACK },
};

// Dual Wielding (#148)
/** Quick Hands: attack cooldowns while dual wielding are this share of normal (10% faster). */
export const QUICK_HANDS_COOLDOWN_FACTOR = 0.9;
/** Twin Strike: the chance a main-hand swing's hit is followed by a free off-hand strike. */
export const TWIN_STRIKE_CHANCE = 0.2;
/** Storm of Blades: Twin Strike's chance once mastered. */
export const STORM_OF_BLADES_TWIN_STRIKE_CHANCE = 0.35;
/** Storm of Blades: how many off-hand strikes one swing can chain into. */
export const STORM_OF_BLADES_MAX_CHAIN = 3;

// Unarmed (#149)
/** Iron Fists: unarmed hits use this many times the base unarmed damage. */
export const IRON_FISTS_DAMAGE_MULTIPLIER = 1.5;
/** Counterstrike: after an attack misses you, your next unarmed hit within this window deals more. */
export const COUNTERSTRIKE_WINDOW_MS = 3000;
export const COUNTERSTRIKE_DAMAGE_BONUS = 0.3;
/** Flowing Stance: stacks from unarmed hits in a row, up to this many. */
export const FLOWING_STANCE_MAX_STACKS = 5;
/** Flowing Stance: the stacks clear this long after your last hit, or at once on a miss. */
export const FLOWING_STANCE_DURATION_MS = 4000;
/** Flowing Stance: avoidance (dodge) each stack adds. */
export const FLOWING_STANCE_AVOIDANCE_PER_STACK = 3;
/** Flowing Stance: share of the attack cooldown each stack takes off. */
export const FLOWING_STANCE_COOLDOWN_REDUCTION_PER_STACK = 0.03;

/** Counterstrike's window: open on the fighter an attack missed. */
export const COUNTERSTRIKE_STACK: BehaviorStackDefinition = { stackId: 'counterstrike', maxStacks: 1, durationMs: COUNTERSTRIKE_WINDOW_MS };
/** Flowing Stance's stacks: dodge and attack speed each. */
export const FLOWING_STANCE_STACK: BehaviorStackDefinition = {
    stackId: 'flowing-stance',
    maxStacks: FLOWING_STANCE_MAX_STACKS,
    durationMs: FLOWING_STANCE_DURATION_MS,
    perStack: { weaponAvoidanceBonus: FLOWING_STANCE_AVOIDANCE_PER_STACK, attackCooldownReduction: FLOWING_STANCE_COOLDOWN_REDUCTION_PER_STACK },
};

// Archery (#150)
/** Steady Aim: standing still this long (without moving or shooting) steadies the next shot. */
export const STEADY_AIM_STILL_MS = 1500;
/** Steady Aim: hit chance a steadied shot gains, in points. */
export const STEADY_AIM_HIT_BONUS = 15;
/** Hunter's Mark: how long a mark lasts after the hit that set it. */
export const HUNTERS_MARK_DURATION_MS = 8000;
/** Hunter's Mark: extra damage your hits deal to a target you've marked. */
export const HUNTERS_MARK_DAMAGE_BONUS = 0.15;
/** Piercing Shot: extra targets an arrow passes through to hit. */
export const PIERCING_SHOT_EXTRA_TARGETS = 1;

/** A hunter's mark on its target: one at a time, the latest archer's. */
export const HUNTERS_MARK_STACK: BehaviorStackDefinition = { stackId: 'hunters-mark', maxStacks: 1, durationMs: HUNTERS_MARK_DURATION_MS };

// Shield Mastery (#151)
/** A partial shield block lets this share of the blow through. */
export const PARTIAL_BLOCK_PASS_FRACTION = 0.5;
/** Brace: a partial block lets only this share through. */
export const BRACE_PARTIAL_BLOCK_PASS_FRACTION = 0.3;
/** Block chance can't rise above this, in percent. */
export const BLOCK_CHANCE_CAP = 65;
/**
 * Bulwark: block chance it adds, and the cap it raises to. Without the bonus
 * the cap would never matter: level 10 alone reaches exactly 65%.
 */
export const BULWARK_BLOCK_CHANCE_BONUS = 10;
export const BULWARK_BLOCK_CHANCE_CAP = 75;
/** Shield Wall: allies this close (tiles) to a blocker are harder to hit. */
export const SHIELD_WALL_RADIUS_TILES = 3;
/** Shield Wall: how long it lasts after the block. */
export const SHIELD_WALL_DURATION_MS = 3000;
/** Shield Wall: avoidance it gives each ally. */
export const SHIELD_WALL_AVOIDANCE_BONUS = 10;

/** Shield Wall's shelter on the blocker's allies. */
export const SHIELD_WALL_STACK: BehaviorStackDefinition = {
    stackId: 'shield-wall',
    maxStacks: 1,
    durationMs: SHIELD_WALL_DURATION_MS,
    perStack: { weaponAvoidanceBonus: SHIELD_WALL_AVOIDANCE_BONUS },
};

// Parry (#152)
/** Parry: the chance per Parry level that a melee weapon turns aside a melee attack (30% at level 10). */
export const PARRY_CHANCE_PER_LEVEL = 0.03;
/** A parry pushes the parrier's next attack back this long, unless they have Quick Recovery. */
export const PARRY_RECOVERY_MS = 300;

// Essence perks (rings 2, 4 and 5 are levels 4, 8 and 10). Ability numbers live in essence-abilities.ts.
// Wrath
/** Searing: your essence hits deal this much more damage. */
export const SEARING_DAMAGE_BONUS = 0.1;
/**
 * Fury's Edge: Bolt of Wrath lances through as many enemies as there are
 * strengths here, each hit at its share of the bolt's damage. Only hits that
 * land count: a missed roll lets it lance on.
 */
export const FURYS_EDGE_HIT_STRENGTHS: readonly number[] = [1, 0.7, 0.4];
/**
 * Cataclysm: at each enemy Bolt of Wrath strikes, a bolt splits off and
 * ricochets into every enemy this close, striking for this share of that
 * hit's damage as it arrives: each enemy at most once a bolt, and never one
 * the bolt already struck.
 */
export const CATACLYSM_RADIUS_TILES = 1.5;
export const CATACLYSM_DAMAGE_FRACTION = 1 / 3;
/** The attack Cataclysm's ricochets fly as: drawn from the enemy struck, not a hand, at the bolt's speed. */
export const BOLT_OF_WRATH_RICOCHET_ATTACK_ID = 'bolt-of-wrath-ricochet';
/** How Cataclysm's ricochets look: their entry in the projectile catalog (#335). */
export const BOLT_OF_WRATH_RICOCHET_PROJECTILE_ID = 'ricochet';
// Frenzy
/** Bloodrush: Frenzy lasts this much longer. */
export const BLOODRUSH_DURATION_BONUS = 0.25;
/** Reckless: while your essence is below this share of your pool, you deal this much more damage. */
export const RECKLESS_ESSENCE_THRESHOLD = 0.5;
export const RECKLESS_DAMAGE_BONUS = 0.15;
// Immolation
/** Kindling: Immolate reaches this much farther, in tiles. */
export const KINDLING_RADIUS_BONUS_TILES = 1;
/** Controlled Burn: allies caught in Immolate take this share, not a third. */
export const CONTROLLED_BURN_ALLY_DAMAGE_FRACTION = 1 / 6;
/** Pyre: Immolate leaves the ground burning this long; enemies in it take this share of the burst each second. */
export const PYRE_DURATION_MS = 4000;
export const PYRE_DAMAGE_FRACTION_PER_SECOND = 1 / 6;
// Battle Instincts
/** Second Wind: dropping below this share of your pool restores this share, at most once per cooldown. */
export const SECOND_WIND_THRESHOLD = 1 / 3;
export const SECOND_WIND_RESTORE_FRACTION = 0.15;
export const SECOND_WIND_COOLDOWN_MS = 60_000;
/** Unshaken: below this share of your pool, you take this much less damage. */
export const UNSHAKEN_ESSENCE_THRESHOLD = 0.5;
export const UNSHAKEN_DAMAGE_TAKEN = -0.15;
// Valor
/** Shoulder the Burden: this share of the damage allies this close to you take falls on you instead. */
export const SHOULDER_THE_BURDEN_FRACTION = 0.15;
export const SHOULDER_THE_BURDEN_RADIUS_TILES = 3;
/** Given Freely: damage you take through Intercede is this much lower. */
export const GIVEN_FREELY_DAMAGE_TAKEN = -0.25;
/** Last Measure: a killing blow meant for an ally this close lands on you instead, once per Crucible. */
export const LAST_MEASURE_RADIUS_TILES = 4;
// Smite
/** Opening Strike: your first hit on an enemy at full health deals this much more. */
export const OPENING_STRIKE_DAMAGE_BONUS = 0.25;
/** Zeal: Smite charges this many hits. */
export const ZEAL_SMITE_CHARGES = 2;
/** Judgment: hits on an enemy below this share of its health deal this much more. */
export const JUDGMENT_HEALTH_THRESHOLD = 0.25;
export const JUDGMENT_DAMAGE_BONUS = 0.4;
// Restoration
/** Mending: heals you cast, and crystals you use, restore this much more. */
export const MENDING_HEAL_BONUS = 0.2;
/** Wellspring: Solace restores this many times as much (30% of the pool, up from 20%). */
export const WELLSPRING_HEAL_MULTIPLIER = 1.5;
/** Lifeline: Solace also heals other allies this close to its target, for its splash share (`SOLACE_SPLASH_HEAL_FRACTION`). */
export const LIFELINE_RADIUS_TILES = 3;
// Entropy
/** Wither: your essence hits lower the target's damage this much for this long. */
export const WITHER_DAMAGE_BONUS = -0.15;
export const WITHER_DURATION_MS = 4000;
/** Dissolution: a kill with an essence hit restores this share of your pool. */
export const DISSOLUTION_RESTORE_FRACTION = 0.1;
// Parting
/** Long Farewell: The Held Door lasts this much longer. */
export const LONG_FAREWELL_DURATION_BONUS = 0.5;
/** Heavy Door: an enemy too strong to hold is slowed to this share of its speed for this long as it crosses. */
export const HEAVY_DOOR_MOVE_SPEED_FACTOR = 0.6;
export const HEAVY_DOOR_SLOW_DURATION_MS = 3000;
/** The Doorway Remembers: enemies too strong to hold wait this long; every enemy that crosses is unveiled this long. */
export const DOORWAY_REMEMBERS_WAIT_MS = 1000;
export const DOORWAY_REMEMBERS_UNVEIL_MS = 6000;
// Insight
/** Keen Eye: Study's weak-point chance rises by this much. */
export const KEEN_EYE_WEAK_POINT_BONUS = 0.1;
/** Studied: Study lasts this many times as long (20s, up from 10s). */
export const STUDIED_DURATION_MULTIPLIER = 2;
/** Foresight: you find weak points without Study, at this chance. */
export const FORESIGHT_WEAK_POINT_CHANCE = 0.1;
// Echo
/** Reverberation: an essence hit has this chance to repeat at this share of its damage. */
export const REVERBERATION_CHANCE = 0.15;
export const REVERBERATION_DAMAGE_FRACTION = 0.5;
/** Lingering: your abilities' timed effects last this much longer. */
export const LINGERING_DURATION_BONUS = 0.15;
// Warding
/** Veil-Sight: Unveil lasts this much longer. */
export const VEIL_SIGHT_DURATION_BONUS = 0.25;
/** Unmasked: unveiled enemies take this much more damage from you and your allies. */
export const UNMASKED_DAMAGE_TAKEN = 0.15;
/** Clarity: Unveil reaches this many times as far. */
export const CLARITY_RADIUS_MULTIPLIER = 2;
// Veilguard
/** Dampen: essence damage you take is this much lower. */
export const DAMPEN_ESSENCE_DAMAGE_TAKEN = -0.1;
/** Absorb: an essence hit you take, or a hit Nullify absorbs, restores this share of your pool. */
export const ABSORB_RESTORE_FRACTION = 0.05;
/** Turn Aside: Nullify absorbs this many hits. */
export const TURN_ASIDE_NULLIFY_CHARGES = 2;
// Attune
/** Gatherer: essence orbs give this much more. */
export const GATHERER_ESSENCE_BONUS = 0.1;
/** Sympathy: Draw pulls this much more. */
export const SYMPATHY_DRAW_BONUS = 0.25;
/** Deep Pool: your essence pool is this much larger. */
export const DEEP_POOL_CAPACITY_BONUS = 0.2;
// Channel
/** Efficient: your abilities cost this much less essence. */
export const EFFICIENT_COST_REDUCTION = 0.1;
/** Focused: your essence hits gain this much hit chance. */
export const FOCUSED_HIT_BONUS = 10;
/** Conduit: while your pool is above this share, your essence hits deal this much more. */
export const CONDUIT_ESSENCE_THRESHOLD = 0.8;
export const CONDUIT_DAMAGE_BONUS = 0.2;
// Harmony
/** Blend: woven abilities cost this share of casting them apart, down from 80%. */
export const BLEND_WEAVE_COST_FRACTION = 0.7;
/** Concord: lent traits are this many times as strong. */
export const CONCORD_TRAIT_STRENGTH = 1.5;
/** Weave blends this many abilities; with Unison, `UNISON_WEAVE_ABILITY_COUNT`. */
export const WEAVE_ABILITY_COUNT = 2;
/** Unison: Weave blends this many abilities. */
export const UNISON_WEAVE_ABILITY_COUNT = 3;
/** Unison: a held pair waits this long for a third, then goes off. */
export const UNISON_THIRD_ABILITY_WAIT_MS = 2000;

export const SKILL_PERKS: readonly SkillPerkDefinition[] = [
    {
        id: 'keen-edge',
        skillId: 'blade-mastery',
        name: 'Keen Edge',
        level: 3,
        description: 'Glancing hits land harder: a blade\'s weakest blow deals 45% of its damage range, not 30%.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'weapon-class', weaponClass: 'blade' }, modifiers: { impactFloor: KEEN_EDGE_IMPACT_FLOOR } }],
    },
    {
        id: 'flurry',
        skillId: 'blade-mastery',
        name: 'Flurry',
        level: 6,
        description: 'Every third blade hit in a row on one target deals +25%. A miss breaks the run.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'all-of', conditions: [{ kind: 'weapon-class', weaponClass: 'blade' }, { kind: 'hit-streak', every: FLURRY_HIT_INTERVAL }] }, modifiers: { landedDamageFactor: 1 + FLURRY_DAMAGE_BONUS } }],
    },
    {
        id: 'find-the-gap',
        skillId: 'blade-mastery',
        name: 'Find the Gap',
        level: 10,
        description: 'Weak-point hits with a blade deal 1.75× instead of 1.5×.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'weapon-class', weaponClass: 'blade' }, modifiers: { weakPointMultiplier: FIND_THE_GAP_WEAK_POINT_MULTIPLIER } }],
    },
    {
        id: 'crushing-blows',
        skillId: 'bludgeoning',
        name: 'Crushing Blows',
        level: 3,
        description: 'Each blunt hit lowers the target\'s mitigation by 2, stacking up to 3 times; the stacks fade 6s after your last blow.',
        behaviors: [{ kind: 'triggered', trigger: 'connect', chance: 1, condition: { kind: 'weapon-class', weaponClass: 'blunt' }, effect: { kind: 'add-stack', on: 'target', stack: MITIGATION_SHRED_STACK } }],
    },
    {
        id: 'stagger',
        skillId: 'bludgeoning',
        name: 'Stagger',
        level: 6,
        description: 'A strong blunt hit pushes the target\'s next attack back half a second.',
        behaviors: [{
            kind: 'triggered',
            trigger: 'connect',
            chance: 1,
            condition: { kind: 'all-of', conditions: [{ kind: 'weapon-class', weaponClass: 'blunt' }, { kind: 'strong-hit', minQuality: STAGGER_MIN_DAMAGE_QUALITY }] },
            effect: { kind: 'stagger', delayMs: STAGGER_DELAY_MS },
        }],
    },
    {
        id: 'guardbreaker',
        skillId: 'bludgeoning',
        name: 'Guardbreaker',
        level: 10,
        description: 'When a shield only partly blocks your blunt blow, it lands in full.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'weapon-class', weaponClass: 'blunt' }, modifiers: { partialBlockBypass: 1 } }],
    },
    {
        id: 'quick-hands',
        skillId: 'dual-wielding',
        name: 'Quick Hands',
        level: 3,
        description: 'While holding two weapons, you attack 10% faster.',
    },
    {
        id: 'twin-strike',
        skillId: 'dual-wielding',
        name: 'Twin Strike',
        level: 6,
        description: 'A main-hand hit has a 20% chance to be followed by a free off-hand strike.',
        behaviors: [{ kind: 'triggered', trigger: 'connect', chance: TWIN_STRIKE_CHANCE, condition: { kind: 'dual-wielding' }, effect: { kind: 'off-hand-strike', maxChain: 1 } }],
    },
    {
        id: 'storm-of-blades',
        skillId: 'dual-wielding',
        name: 'Storm of Blades',
        level: 10,
        description: 'Twin Strike follows up 35% of the time, and a follow-up can chain into another, up to 3.',
        behaviors: [{ kind: 'triggered', trigger: 'connect', chance: STORM_OF_BLADES_TWIN_STRIKE_CHANCE, condition: { kind: 'dual-wielding' }, effect: { kind: 'off-hand-strike', maxChain: STORM_OF_BLADES_MAX_CHAIN } }],
    },
    {
        id: 'iron-fists',
        skillId: 'unarmed',
        name: 'Iron Fists',
        level: 3,
        description: 'Your bare-handed blows deal 50% more base damage.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'weapon-class', weaponClass: 'unarmed' }, modifiers: { baseDamageMultiplier: IRON_FISTS_DAMAGE_MULTIPLIER - 1 } }],
    },
    {
        id: 'counterstrike',
        skillId: 'unarmed',
        name: 'Counterstrike',
        level: 6,
        description: 'After an attack misses you, your next unarmed hit within 3s deals +30%.',
        behaviors: [
            { kind: 'triggered', trigger: 'dodged', chance: 1, effect: { kind: 'add-stack', on: 'self', stack: COUNTERSTRIKE_STACK } },
            {
                kind: 'conditional',
                condition: { kind: 'all-of', conditions: [{ kind: 'weapon-class', weaponClass: 'unarmed' }, { kind: 'has-stack', on: 'self', stackId: COUNTERSTRIKE_STACK.stackId }] },
                modifiers: { landedDamageFactor: 1 + COUNTERSTRIKE_DAMAGE_BONUS },
            },
            // The next unarmed hit spends the window.
            { kind: 'triggered', trigger: 'connect', chance: 1, condition: { kind: 'weapon-class', weaponClass: 'unarmed' }, effect: { kind: 'clear-stack', on: 'self', stackId: COUNTERSTRIKE_STACK.stackId } },
        ],
    },
    {
        id: 'flowing-stance',
        skillId: 'unarmed',
        name: 'Flowing Stance',
        level: 10,
        description: 'Each unarmed hit in a row adds a stack, up to 5: +3 dodge and 3% faster attacks each. A miss, or 4s without a hit, clears them.',
        behaviors: [
            { kind: 'triggered', trigger: 'connect', chance: 1, condition: { kind: 'weapon-class', weaponClass: 'unarmed' }, effect: { kind: 'add-stack', on: 'self', stack: FLOWING_STANCE_STACK } },
            { kind: 'triggered', trigger: 'miss', chance: 1, condition: { kind: 'weapon-class', weaponClass: 'unarmed' }, effect: { kind: 'clear-stack', on: 'self', stackId: FLOWING_STANCE_STACK.stackId } },
        ],
    },
    {
        id: 'steady-aim',
        skillId: 'archery',
        name: 'Steady Aim',
        level: 3,
        description: 'After 1.5s standing still without shooting, your next shot gets +15 hit chance.',
        behaviors: [{
            kind: 'conditional',
            condition: { kind: 'all-of', conditions: [{ kind: 'attack-style', styles: ['range'] }, { kind: 'standing-still', ms: STEADY_AIM_STILL_MS, countsShots: true }] },
            modifiers: { hitBonus: STEADY_AIM_HIT_BONUS },
        }],
    },
    {
        id: 'hunters-mark',
        skillId: 'archery',
        name: 'Hunter\'s Mark',
        level: 6,
        description: 'An arrow that hits marks its target for 8s; your hits on a target you marked deal +15%.',
        behaviors: [
            { kind: 'triggered', trigger: 'connect', chance: 1, condition: { kind: 'attack-style', styles: ['range'] }, effect: { kind: 'add-stack', on: 'target', stack: HUNTERS_MARK_STACK } },
            {
                kind: 'conditional',
                condition: { kind: 'all-of', conditions: [{ kind: 'attack-style', styles: ['melee', 'range'] }, { kind: 'has-stack', on: 'target', stackId: HUNTERS_MARK_STACK.stackId, fromWearer: true }] },
                modifiers: { landedDamageFactor: 1 + HUNTERS_MARK_DAMAGE_BONUS },
            },
        ],
    },
    {
        id: 'piercing-shot',
        skillId: 'archery',
        name: 'Piercing Shot',
        level: 10,
        description: 'Your arrows pass through the first target and hit the next one in their path.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'attack-style', styles: ['range'] }, modifiers: { extraTargets: PIERCING_SHOT_EXTRA_TARGETS } }],
    },
    {
        id: 'brace',
        skillId: 'shield-mastery',
        name: 'Brace',
        level: 3,
        description: 'A partial block lets through 30% of the blow, not 50%.',
        behaviors: [{ kind: 'conditional', modifiers: { partialBlockPassFraction: BRACE_PARTIAL_BLOCK_PASS_FRACTION } }],
    },
    {
        id: 'shield-wall',
        skillId: 'shield-mastery',
        name: 'Shield Wall',
        level: 6,
        description: 'When you block, allies within 3 tiles get +10 dodge for 3s.',
        behaviors: [{ kind: 'triggered', trigger: 'blocked', chance: 1, effect: { kind: 'add-stack', on: 'allies', radiusTiles: SHIELD_WALL_RADIUS_TILES, stack: SHIELD_WALL_STACK } }],
    },
    {
        id: 'bulwark',
        skillId: 'shield-mastery',
        name: 'Bulwark',
        level: 10,
        description: 'Your block chance rises by 10, up to 75% rather than 65%, and a full block staggers the attacker.',
        behaviors: [
            { kind: 'conditional', modifiers: { blockChanceBonus: BULWARK_BLOCK_CHANCE_BONUS, blockChanceCap: BULWARK_BLOCK_CHANCE_CAP } },
            { kind: 'triggered', trigger: 'blocked', blockType: 'full', chance: 1, effect: { kind: 'stagger', delayMs: STAGGER_DELAY_MS } },
        ],
    },
    {
        id: 'quick-recovery',
        skillId: 'parry',
        name: 'Quick Recovery',
        level: 3,
        description: 'A parry no longer slows your next attack (normally it pushes it back 0.3s).',
        behaviors: [{ kind: 'conditional', modifiers: { parryRecoveryFactor: 0 } }],
    },
    {
        id: 'riposte',
        skillId: 'parry',
        name: 'Riposte',
        level: 6,
        description: 'A successful parry gives you a free counterattack on the attacker.',
        behaviors: [{ kind: 'triggered', trigger: 'parried', chance: 1, effect: { kind: 'counterattack' } }],
    },
    {
        id: 'perfect-parry',
        skillId: 'parry',
        name: 'Perfect Parry',
        level: 10,
        description: 'Parried attackers are staggered, and you can turn aside arrows too.',
        behaviors: [
            { kind: 'conditional', modifiers: { rangedParryShare: 1 } },
            { kind: 'triggered', trigger: 'parried', chance: 1, effect: { kind: 'stagger', delayMs: STAGGER_DELAY_MS } },
        ],
    },
    {
        id: 'searing',
        skillId: 'wrath',
        name: 'Searing',
        level: 4,
        description: 'Your essence hits deal 10% more damage.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'attack-style', styles: ['essence'] }, modifiers: { damageMultiplier: SEARING_DAMAGE_BONUS } }],
    },
    {
        id: 'furys-edge',
        skillId: 'wrath',
        name: 'Fury\'s Edge',
        level: 8,
        description: 'Bolt of Wrath lances through up to 3 enemies, each hit weaker than the last.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'bolt-of-wrath', change: { hitStrengths: FURYS_EDGE_HIT_STRENGTHS } }],
    },
    {
        id: 'cataclysm',
        skillId: 'wrath',
        name: 'Cataclysm',
        level: 10,
        description: 'At each enemy Bolt of Wrath strikes, bolts ricochet into the enemies within 1.5 tiles for a third of that hit.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'bolt-of-wrath', change: { riders: [{ kind: 'ricochet', attackId: BOLT_OF_WRATH_RICOCHET_ATTACK_ID, projectileId: BOLT_OF_WRATH_RICOCHET_PROJECTILE_ID, radiusTiles: CATACLYSM_RADIUS_TILES, damageFraction: CATACLYSM_DAMAGE_FRACTION }] } }],
    },
    {
        id: 'bloodrush',
        skillId: 'frenzy',
        name: 'Bloodrush',
        level: 4,
        description: 'Frenzy lasts 25% longer.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'frenzy', change: { durationMultiplier: 1 + BLOODRUSH_DURATION_BONUS } }],
    },
    {
        id: 'reckless',
        skillId: 'frenzy',
        name: 'Reckless',
        level: 8,
        description: 'While your essence is below half, you deal 15% more damage.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'essence-below', poolShare: RECKLESS_ESSENCE_THRESHOLD }, modifiers: { damageMultiplier: RECKLESS_DAMAGE_BONUS } }],
    },
    {
        id: 'unbridled',
        skillId: 'frenzy',
        name: 'Unbridled',
        level: 10,
        description: 'Frenzy no longer raises the damage you take.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'frenzy', change: { hasNoDrawback: true } }],
    },
    {
        id: 'kindling',
        skillId: 'immolation',
        name: 'Kindling',
        level: 4,
        description: 'Immolate reaches 1 tile farther.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'immolate', change: { radiusBonusTiles: KINDLING_RADIUS_BONUS_TILES } }],
    },
    {
        id: 'controlled-burn',
        skillId: 'immolation',
        name: 'Controlled Burn',
        level: 8,
        description: 'Allies caught in Immolate take a sixth of its damage, not a third.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'immolate', change: { allyDamageFraction: CONTROLLED_BURN_ALLY_DAMAGE_FRACTION } }],
    },
    {
        id: 'pyre',
        skillId: 'immolation',
        name: 'Pyre',
        level: 10,
        description: 'Immolate leaves the ground burning for 4s: enemies in it take a sixth of the burst each second.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'immolate', change: { riders: [{ kind: 'burning-ground', durationMs: PYRE_DURATION_MS, damageFractionPerSecond: PYRE_DAMAGE_FRACTION_PER_SECOND }] } }],
    },
    {
        id: 'second-wind',
        skillId: 'battle-instincts',
        name: 'Second Wind',
        level: 4,
        description: 'The first time your essence drops below a third, recover 15% of your pool. Ready again after a minute.',
        behaviors: [{ kind: 'rule-override', rule: 'second-wind' }],
    },
    {
        id: 'unshaken',
        skillId: 'battle-instincts',
        name: 'Unshaken',
        level: 8,
        description: 'While your essence is below half, you take 15% less damage.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'essence-below', poolShare: UNSHAKEN_ESSENCE_THRESHOLD }, modifiers: { damageTakenMultiplier: UNSHAKEN_DAMAGE_TAKEN } }],
    },
    {
        id: 'indomitable',
        skillId: 'battle-instincts',
        name: 'Indomitable',
        level: 10,
        description: 'Once per Crucible, a killing blow leaves you at 1 essence instead.',
        behaviors: [{ kind: 'rule-override', rule: 'survive-a-killing-blow' }],
    },
    {
        id: 'shoulder-the-burden',
        skillId: 'valor',
        name: 'Shoulder the Burden',
        level: 4,
        description: '15% of the damage allies within 3 tiles of you take falls on you instead.',
        behaviors: [{ kind: 'rule-override', rule: 'shoulder-allies-damage' }],
    },
    {
        id: 'given-freely',
        skillId: 'valor',
        name: 'Given Freely',
        level: 8,
        description: 'Damage you take through Intercede is a quarter lower.',
        behaviors: [{ kind: 'rule-override', rule: 'intercede-takes-less' }],
    },
    {
        id: 'last-measure',
        skillId: 'valor',
        name: 'Last Measure',
        level: 10,
        description: 'Once per Crucible, a killing blow meant for an ally within 4 tiles lands on you instead, even if it fells you.',
        behaviors: [{ kind: 'rule-override', rule: 'take-allies-killing-blow' }],
    },
    {
        id: 'opening-strike',
        skillId: 'smite',
        name: 'Opening Strike',
        level: 4,
        description: 'Your first hit on an enemy at full health deals 25% more.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'all-of', conditions: [{ kind: 'attack-style', styles: ['melee', 'range'] }, { kind: 'target-unhurt' }] }, modifiers: { landedDamageFactor: 1 + OPENING_STRIKE_DAMAGE_BONUS } }],
    },
    {
        id: 'zeal',
        skillId: 'smite',
        name: 'Zeal',
        level: 8,
        description: 'Smite charges your next two hits, not one.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'smite', change: { extraCharges: ZEAL_SMITE_CHARGES - 1 } }],
    },
    {
        id: 'judgment',
        skillId: 'smite',
        name: 'Judgment',
        level: 10,
        description: 'Your hits on an enemy below a quarter of its health deal 40% more.',
        behaviors: [{
            kind: 'conditional',
            condition: { kind: 'all-of', conditions: [{ kind: 'attack-style', styles: ['melee', 'range'] }, { kind: 'target-essence-below', poolShare: JUDGMENT_HEALTH_THRESHOLD }] },
            modifiers: { landedDamageFactor: 1 + JUDGMENT_DAMAGE_BONUS },
        }],
    },
    {
        id: 'mending',
        skillId: 'restoration',
        name: 'Mending',
        level: 4,
        description: 'Your heals, and crystals you use, restore 20% more.',
    },
    {
        id: 'wellspring',
        skillId: 'restoration',
        name: 'Wellspring',
        level: 8,
        description: 'Solace restores 30% of the pool, not 20%.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'solace', change: { healMultiplier: WELLSPRING_HEAL_MULTIPLIER } }],
    },
    {
        id: 'lifeline',
        skillId: 'restoration',
        name: 'Lifeline',
        level: 10,
        description: 'Solace also heals every other ally within 3 tiles of its target, for half as much.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'solace', change: { riders: [{ kind: 'heal-nearby', radiusTiles: LIFELINE_RADIUS_TILES }] } }],
    },
    {
        id: 'wither',
        skillId: 'entropy',
        name: 'Wither',
        level: 4,
        description: 'Your essence hits lower the target\'s damage by 15% for 4s.',
        behaviors: [{
            kind: 'triggered',
            trigger: 'connect',
            chance: 1,
            condition: { kind: 'attack-style', styles: ['essence'] },
            effect: { kind: 'status', on: 'target', statusKind: 'wither', modifiers: { damageMultiplier: WITHER_DAMAGE_BONUS }, durationMs: WITHER_DURATION_MS },
        }],
    },
    {
        id: 'unravel',
        skillId: 'entropy',
        name: 'Unravel',
        level: 8,
        description: 'Your essence hits lower the target\'s mitigation, stacking up to 3 with Crushing Blows.',
        behaviors: [{ kind: 'triggered', trigger: 'connect', chance: 1, condition: { kind: 'attack-style', styles: ['essence'] }, effect: { kind: 'add-stack', on: 'target', stack: MITIGATION_SHRED_STACK } }],
    },
    {
        id: 'dissolution',
        skillId: 'entropy',
        name: 'Dissolution',
        level: 10,
        description: 'A kill with an essence hit restores 10% of your pool.',
        behaviors: [{ kind: 'rule-override', rule: 'essence-kills-restore-essence' }],
    },
    {
        id: 'long-farewell',
        skillId: 'parting',
        name: 'Long Farewell',
        level: 4,
        description: 'The Held Door lasts 50% longer.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'held-door', change: { durationMultiplier: 1 + LONG_FAREWELL_DURATION_BONUS } }],
    },
    {
        id: 'heavy-door',
        skillId: 'parting',
        name: 'Heavy Door',
        level: 8,
        description: 'Enemies too strong for The Held Door to hold are slowed 40% for 3s as they cross.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'held-door', change: { riders: [{ kind: 'slow-unheld-crossers', durationMs: HEAVY_DOOR_SLOW_DURATION_MS, moveSpeedFactor: HEAVY_DOOR_MOVE_SPEED_FACTOR }] } }],
    },
    {
        id: 'doorway-remembers',
        skillId: 'parting',
        name: 'The Doorway Remembers',
        level: 10,
        description: 'Enemies too strong to hold still wait 1s at The Held Door, and every enemy that crosses it is unveiled to your party for 6s.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'held-door', change: { riders: [{ kind: 'remembering-door', unveilMs: DOORWAY_REMEMBERS_UNVEIL_MS }] } }],
    },
    {
        id: 'keen-eye',
        skillId: 'insight',
        name: 'Keen Eye',
        level: 4,
        description: 'Your party strikes weak points on enemies you Study 10 points more often.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'study', change: { magnitudeBonus: KEEN_EYE_WEAK_POINT_BONUS } }],
    },
    {
        id: 'studied',
        skillId: 'insight',
        name: 'Studied',
        level: 8,
        description: 'Study lasts 20s, not 10s.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'study', change: { durationMultiplier: STUDIED_DURATION_MULTIPLIER } }],
    },
    {
        id: 'foresight',
        skillId: 'insight',
        name: 'Foresight',
        level: 10,
        description: 'You find weak points without Study, at a 10% chance.',
        behaviors: [{ kind: 'conditional', modifiers: { weakPointChance: FORESIGHT_WEAK_POINT_CHANCE } }],
    },
    {
        id: 'reverberation',
        skillId: 'echo',
        name: 'Reverberation',
        level: 4,
        description: 'Your essence hits have a 15% chance to repeat at half damage.',
        behaviors: [{ kind: 'triggered', trigger: 'connect', chance: REVERBERATION_CHANCE, condition: { kind: 'attack-style', styles: ['essence'] }, effect: { kind: 'repeat-hit', damageFraction: REVERBERATION_DAMAGE_FRACTION } }],
    },
    {
        id: 'lingering',
        skillId: 'echo',
        name: 'Lingering',
        level: 8,
        description: 'Your abilities\' timed effects last 15% longer.',
        behaviors: [{ kind: 'every-ability-modifier', change: { durationMultiplier: 1 + LINGERING_DURATION_BONUS } }],
    },
    {
        id: 'recall',
        skillId: 'echo',
        name: 'Recall',
        level: 10,
        description: 'Echo\'s repeat comes at full strength.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'echo', change: { repeatPower: 1 } }],
    },
    {
        id: 'veil-sight',
        skillId: 'warding',
        name: 'Veil-Sight',
        level: 4,
        description: 'Unveil lasts 25% longer.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'unveil', change: { durationMultiplier: 1 + VEIL_SIGHT_DURATION_BONUS } }],
    },
    {
        id: 'unmasked',
        skillId: 'warding',
        name: 'Unmasked',
        level: 8,
        description: 'Enemies you unveil take 15% more damage from you and your allies.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'unveil', change: { statusModifiers: { damageTakenMultiplier: UNMASKED_DAMAGE_TAKEN } } }],
    },
    {
        id: 'clarity',
        skillId: 'warding',
        name: 'Clarity',
        level: 10,
        description: 'Unveil reaches twice as far.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'unveil', change: { radiusMultiplier: CLARITY_RADIUS_MULTIPLIER } }],
    },
    {
        id: 'dampen',
        skillId: 'veilguard',
        name: 'Dampen',
        level: 4,
        description: 'Essence damage you take is 10% lower.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'attack-style', styles: ['essence'] }, modifiers: { damageTakenMultiplier: DAMPEN_ESSENCE_DAMAGE_TAKEN } }],
    },
    {
        id: 'absorb',
        skillId: 'veilguard',
        name: 'Absorb',
        level: 8,
        description: 'An essence hit you take, or a hit Nullify absorbs, restores 5% of your pool.',
        behaviors: [{ kind: 'rule-override', rule: 'absorbing-restores-essence' }],
    },
    {
        id: 'turn-aside',
        skillId: 'veilguard',
        name: 'Turn Aside',
        level: 10,
        description: 'Nullify absorbs two hits, not one.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'nullify', change: { extraCharges: TURN_ASIDE_NULLIFY_CHARGES - 1 } }],
    },
    {
        id: 'gatherer',
        skillId: 'attune',
        name: 'Gatherer',
        level: 4,
        description: 'Essence orbs give you 10% more.',
    },
    {
        id: 'sympathy',
        skillId: 'attune',
        name: 'Sympathy',
        level: 8,
        description: 'Draw pulls 25% more essence.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'draw', change: { healMultiplier: 1 + SYMPATHY_DRAW_BONUS } }],
    },
    {
        id: 'deep-pool',
        skillId: 'attune',
        name: 'Deep Pool',
        level: 10,
        description: 'Your essence pool is 20% larger.',
    },
    {
        id: 'efficient',
        skillId: 'channel',
        name: 'Efficient',
        level: 4,
        description: 'Your abilities cost 10% less essence.',
        behaviors: [{ kind: 'every-ability-modifier', change: { costMultiplier: 1 - EFFICIENT_COST_REDUCTION } }],
    },
    {
        id: 'focused',
        skillId: 'channel',
        name: 'Focused',
        level: 8,
        description: 'Your essence hits gain 10 hit chance.',
        behaviors: [{ kind: 'conditional', condition: { kind: 'attack-style', styles: ['essence'] }, modifiers: { hitBonus: FOCUSED_HIT_BONUS } }],
    },
    {
        id: 'conduit',
        skillId: 'channel',
        name: 'Conduit',
        level: 10,
        description: 'While your pool is above 80%, your essence hits deal 20% more.',
        behaviors: [{
            kind: 'conditional',
            condition: { kind: 'all-of', conditions: [{ kind: 'attack-style', styles: ['essence'] }, { kind: 'essence-above', poolShare: CONDUIT_ESSENCE_THRESHOLD }] },
            modifiers: { damageMultiplier: CONDUIT_DAMAGE_BONUS },
        }],
    },
    {
        id: 'blend',
        skillId: 'harmony',
        name: 'Blend',
        level: 4,
        description: 'Woven abilities cost 70% of casting them apart, not 80%.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'weave', change: { weaveCostFraction: BLEND_WEAVE_COST_FRACTION } }],
    },
    {
        id: 'concord',
        skillId: 'harmony',
        name: 'Concord',
        level: 8,
        description: 'Lent traits are half again as strong.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'weave', change: { traitStrengthMultiplier: CONCORD_TRAIT_STRENGTH } }],
    },
    {
        id: 'unison',
        skillId: 'harmony',
        name: 'Unison',
        level: 10,
        description: 'Weave takes three abilities, each lent both other traits.',
        behaviors: [{ kind: 'ability-modifier', abilityId: 'weave', change: { extraWovenAbilities: UNISON_WEAVE_ABILITY_COUNT - WEAVE_ABILITY_COUNT } }],
    },
];

/** A skill's perks, in the order they unlock. */
export function findSkillPerks(skillId: string): SkillPerkDefinition[] {
    return SKILL_PERKS.filter((perk) => perk.skillId === skillId).sort((a, b) => a.level - b.level);
}

/** Whether perk `perkId` is active: its skill is at or above the perk's level. */
export function hasSkillPerk(skills: readonly Skill[] | undefined, perkId: string): boolean {
    const perk = SKILL_PERKS.find((candidate) => candidate.id === perkId);
    if (!perk) {
        return false;
    }
    const level = skills?.find((skill) => skill.id === perk.skillId)?.level ?? 0;
    return level >= perk.level;
}

/** Weapon families that train Blade Mastery. */
export const BLADE_WEAPON_CLASSES: readonly string[] = ['sword', 'dagger', 'axe'];
/** Weapon families that train Bludgeoning. */
export const BLUNT_WEAPON_CLASSES: readonly string[] = ['mace', 'hammer'];

/**
 * The Martial skills a melee hit with `weapon` trains: bare hands train
 * Unarmed, blades Blade Mastery, maces and hammers Bludgeoning. A weapon
 * with no family yet trains both weapon skills, as every melee hit did
 * before skills told them apart.
 */
export function resolveMeleeWeaponSkillIds(weapon: Equipment | undefined): string[] {
    if (!weapon) {
        return ['unarmed'];
    }
    const weaponClass = weapon.combat?.weaponClass;
    if (weaponClass && BLADE_WEAPON_CLASSES.includes(weaponClass)) {
        return ['blade-mastery'];
    }
    if (weaponClass && BLUNT_WEAPON_CLASSES.includes(weaponClass)) {
        return ['bludgeoning'];
    }
    return ['blade-mastery', 'bludgeoning'];
}

/** Whether a character holds a weapon in each hand (a shield doesn't count). */
export function isDualWielding(equipped: CharacterEquipmentLoadout | undefined): boolean {
    const [mainHand, offHand] = equipped?.weapons ?? [];
    return isEquippedItem(mainHand) && isEquippedItem(offHand) && offHand.equipmentType !== 'shield';
}

/** Whether `weapon` is a blunt weapon (mace or hammer). */
export function isBluntWeapon(weapon: Equipment | undefined): boolean {
    const weaponClass = weapon?.combat?.weaponClass;
    return weaponClass !== undefined && BLUNT_WEAPON_CLASSES.includes(weaponClass);
}

/** Whether `weapon` is a blade (sword or dagger). */
export function isBladeWeapon(weapon: Equipment | undefined): boolean {
    const weaponClass = weapon?.combat?.weaponClass;
    return weaponClass !== undefined && BLADE_WEAPON_CLASSES.includes(weaponClass);
}
